<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { base64ToFile } from "@/common/utils/files";

import type { FileType } from "@/common/types/common";
import {
  sampleAdd,
  sampleDel,
  sampleList,
  samplePut,
  sampleModelCSV,
  sampleUpload,
} from "@/common/api/rest/sample";
import type {
  SampleListType,
  SamplePayloadType,
  SampleType,
} from "@/common/types/sample";

import { RegisterTheme } from "@/common/themes";
import { fileUploadForm, sampleForm } from "@/components";
import {
  baseAvatar,
  baseTable,
  baseConfirm,
  baseModal,
  svgIcon,
} from "@/common/components";

import { statusIcons, orderIcons } from "@/common/settings/register";

const { t, tm } = useI18n();
const route = useRoute();
const router = useRouter();

const tableData = ref<SampleListType[]>([]);

const selected = ref<SampleListType | null>(null);
const payload = ref<SamplePayloadType | null>(null);

const fileUpload = ref<FileType | null>(null);
const fileExtensions = ref(["csv"]);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const isEdit = ref(false);
const loading = ref(false);
const showModal = ref(false);
const showConfirmDel = ref(false);
const showModalUpload = ref(false);

const id = computed(() => route.params.id as string);

const tableHeader = computed(() => [
  { key: "logo", label: null },
  { key: "code", label: t("common.labels.code") },
  { key: "name", label: t("common.labels.name") },
  { key: "status", label: t("common.labels.status") },
]);

const modalTitle = computed(() =>
  isEdit.value
    ? t("common.labels.edit", {
        concat: t("common.labels.sample"),
      }).toUpperCase()
    : t("common.labels.add", {
        concat: t("common.labels.sample"),
      }).toUpperCase(),
);

const itemStatus = computed(
  () => tm("enums.item_status") as Record<string, string>,
);

function fetchData() {
  sampleList(id.value, {
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loading.value = false;
    });
}

function add() {
  showModal.value = true;
  payload.value = null;
}

function addConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  sampleAdd(id.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        router.push(`/${id.value}/register/sample/edit/${data.sample_id}`);
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function edit(data: SampleListType) {
  router.push(`/${id.value}/register/sample/edit/${data.sample_id}`);
}

function editConfirm() {
  if (!selected.value && !payload.value) return;

  store.commit("SET_LOADING", true);

  samplePut(
    id.value,
    selected.value?.sample_id as string,
    payload.value as SamplePayloadType,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        closeModal();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function submit() {
  if (!isEdit.value) addConfirm();
  else editConfirm();
}

function closeModal() {
  payload.value = null;
  showModal.value = false;
  isEdit.value = false;
}

function remove(data: SampleListType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);

  sampleDel(id.value, selected.value.sample_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function searchItem(value: string) {
  search.value = value;
  tableData.value = [];
  loading.value = true;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  loading.value = true;
  fetchData();
}

function uploadCsv() {
  showModalUpload.value = true;
}

function uploadCancel() {
  showModalUpload.value = false;
  fileUpload.value = null;
}

function uploadConfirm() {
  if (!fileUpload.value) return;

  store.commit("SET_LOADING", true);
  sampleUpload(id.value, fileUpload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        uploadCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function downloadModel() {
  sampleModelCSV(id.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        base64ToFile(data.file, data.name, data.type);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <register-theme tab-active="sample">
    <template #title>
      <svg-icon icon="samples" size="2rem" />
      {{ t("common.labels.samples") }}
    </template>

    <template #content>
      <base-table
        :header="tableHeader"
        :data="tableData"
        :limit="limit"
        :page="page"
        :pages="pages"
        :total="total"
        :searched="searched"
        :options="true"
        :edit="true"
        :del="true"
        :upload="true"
        :loading="loading"
        @change:page="changePage"
        @searching="searchItem"
        @btn:add="add"
        @btn:edit="edit"
        @btn:del="remove"
        @btn:upload="uploadCsv()"
      >
        <template #logo="{ data }">
          <base-avatar
            :url="data.avatar?.small"
            size="1.75rem"
            background="#ffffff"
          />
        </template>
        <template #status="{ data }">
          {{ itemStatus[data.status] }}
        </template>
      </base-table>
    </template>
  </register-theme>

  <base-confirm
    :show="showConfirmDel"
    :title="
      t('common.labels.remove', {
        concat: t('common.labels.sample'),
      }).toUpperCase()
    "
    :message="
      t('common.texts.remove_message', {
        concat: selected?.code,
      })
    "
    @cancel="removeCancel"
    @confirm="removeConfirm"
  />

  <base-modal
    :show="showModal"
    :title="modalTitle"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.save')"
    :button-close="t('common.labels.cancel')"
    @close="closeModal()"
    @confirm="submit()"
  >
    <template #content>
      <sample-form :data="payload" @update-data="payload = $event" />
    </template>
  </base-modal>

  <base-modal
    :show="showModalUpload"
    :title="
      t('common.labels.upload', {
        concat: t('common.labels.sample'),
      }).toUpperCase()
    "
    :show-button-confirm="true"
    :button-confirm="t('common.labels.send')"
    :button-close="t('common.labels.cancel')"
    @close="uploadCancel()"
    @confirm="uploadConfirm()"
  >
    <template #content>
      <file-upload-form
        :extensions="fileExtensions"
        :model="true"
        @update-data="fileUpload = $event"
        @model-download="downloadModel()"
      />
    </template>
  </base-modal>
</template>

<style lang="scss" scoped>
.status {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
}
</style>
